<script setup>
import { computed, defineProps, defineEmits } from 'vue';
import { WIDGET_TYPES } from '@/components/widgets';
import EventLogWidget from '@/components/widgets/eventLog/EventLogWidget.vue';
import EventsWidget from '@/components/widgets/events/EventsWidget.vue';
import GaugeWidget from '@/components/widgets/gauge/GaugeWidget.vue';
import MapWidget from '@/components/widgets/map/MapWidget.vue';
import StatsCardWidget from '@/components/widgets/statsCard/StatsCardWidget.vue';
import StatusWidget from '@/components/widgets/status/StatusWidget.vue';
import TableWidget from '@/components/widgets/table/TableWidget.vue';
import TimeseriesWidget from '@/components/widgets/timeseries/TimeseriesWidget.vue';

const props = defineProps({
  widget: {
    type: Object,
    required: true
  },
  widgetId: {
    type: String,
    default: null
  },
  dataID: {
    type: String,
    required: true
  },
  timeFormat: {
    type: String,
    default: '24hour'
  },
  streamData: {
    type: Object,
    default: () => ({})
  },
  historicalData: {
    type: Object,
    default: () => ({})
  },
  editMode: {
    type: Boolean,
    default: false
  },
  sourceErrors: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['updateWidget']);

const widgetType = computed(() => props.widget.type);
const isLoading = computed(() => props.widget._loading === true);
const INTERNALLY_HANDLED_TYPES = new Set([
  WIDGET_TYPES.GAUGE,
  WIDGET_TYPES.STATUS,
  WIDGET_TYPES.STATS_CARD,
  WIDGET_TYPES.TABLE,
  WIDGET_TYPES.MAP,
]);

const notFoundSource = computed(() => {
  if (!props.sourceErrors || !props.widget.source) return null;
  if (INTERNALLY_HANDLED_TYPES.has(props.widget.type)) return null;
  const entry = props.sourceErrors[props.widget.source];
  return entry?.type === 'not_found' ? props.widget.source : null;
});

function handleEventsWidgetUpdate(updatedWidget) {
  if (props.widgetId != null) {
    emit('updateWidget', { widgetId: props.widgetId, widget: updatedWidget });
  }
}
</script>

<template>
  <div class="w-full h-full relative">
    <!-- source-not-found error -->
    <div
      v-if="notFoundSource"
      class="flex flex-col items-center justify-center h-full gap-2 bg-mc-cloud dark:bg-mc-charcoal rounded-mc-sm"
    >
      <p class="font-body text-tier-2 text-mc-red font-medium">Source Not Found</p>
      <p class="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted">{{ notFoundSource }}</p>
    </div>

    <!-- Loading Overlay -->
    <div
      v-else-if="isLoading"
      class="absolute inset-0 bg-mc-white/70 dark:bg-mc-charcoal/70 backdrop-blur-sm flex items-center justify-center z-10 rounded-mc-sm"
    >
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-mc-red mx-auto mb-3"></div>
        <p class="text-mc-ink dark:text-on-dark font-body font-medium">Creating widget...</p>
      </div>
    </div>

    <!-- Event Log Widget -->
    <EventLogWidget
      v-else-if="widgetType === WIDGET_TYPES.EVENT_LOG"
      :widget="widget"
      :dataID="dataID"
      :timeFormat="timeFormat"
    />

    <!-- Gauge Widget -->
    <GaugeWidget
      v-else-if="widgetType === WIDGET_TYPES.GAUGE"
      :widget="widget"
      :dataID="dataID"
      :timeFormat="timeFormat"
      :streamData="streamData"
      :sourceErrors="sourceErrors"
    />

    <!-- Stats Card Widget -->
    <StatsCardWidget
      v-else-if="widgetType === WIDGET_TYPES.STATS_CARD"
      :widget="widget"
      :dataID="dataID"
      :timeFormat="timeFormat"
      :streamData="streamData"
      :sourceErrors="sourceErrors"
    />

    <!-- Map Widget -->
    <MapWidget
      v-else-if="widgetType === WIDGET_TYPES.MAP"
      :widget="widget"
      :dataID="dataID"
      :timeFormat="timeFormat"
      :streamData="streamData"
      :sourceErrors="sourceErrors"
    />

    <!-- Status Widget -->
    <StatusWidget
      v-else-if="widgetType === WIDGET_TYPES.STATUS"
      :widget="widget"
      :dataID="dataID"
      :timeFormat="timeFormat"
      :streamData="streamData"
      :sourceErrors="sourceErrors"
    />

    <!-- Table Widget -->
    <TableWidget
      v-else-if="widgetType === WIDGET_TYPES.TABLE"
      :widget="widget"
      :dataID="dataID"
      :timeFormat="timeFormat"
      :streamData="streamData"
      :sourceErrors="sourceErrors"
    />

    <TimeseriesWidget
      v-else-if="widgetType === WIDGET_TYPES.TIMESERIES"
      :widget="widget"
      :dataID="dataID"
      :timeFormat="timeFormat"
      :streamData="streamData"
      :historicalData="historicalData"
      :editMode="editMode"
      :sourceErrors="sourceErrors"
    />

    <!-- Events Widget -->
    <EventsWidget
      v-else-if="widgetType === WIDGET_TYPES.EVENTS"
      :widget="widget"
      :dataID="dataID"
      :timeFormat="timeFormat"
      :streamData="streamData"
      :sourceErrors="sourceErrors"
      @update="handleEventsWidgetUpdate"
    />

    <!-- Unknown widget type -->
    <div v-else class="flex items-center justify-center h-full bg-mc-cloud dark:bg-mc-charcoal rounded-mc-sm">
      <p class="text-ink-muted dark:text-on-dark-muted">Unknown widget type: {{ widgetType }}</p>
    </div>
  </div>
</template>
